import React from 'react';
import {
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter,
  DrawerCloseButton, Button, VStack, Divider,
  useDisclosure,
} from '@chakra-ui/react';
import { FiUsers, FiDatabase, FiSliders, FiCreditCard, FiPercent, FiDollarSign, FiGrid, FiFilter, FiEye, FiMail, FiGitMerge, FiHeart, FiRepeat, FiBriefcase, FiShield, FiActivity, FiTruck, FiCopy, FiList , FiSlash } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import UserManagementModal from './UserManagementModal';
import BackupRestoreModal from './BackupRestoreModal';
import AdminSettingsModal from './AdminSettingsModal';
import BankAccountsModal from './BankAccountsModal';
import VatRatesModal from './VatRatesModal';
import CurrencyModal from './CurrencyModal';
import RowGroupsModal from './RowGroupsModal';
import ViewFiltersModal from './ViewFiltersModal';
import AccessibilityModal from './AccessibilityModal';
import EmailAccountsModal from './EmailAccountsModal';
import EmailRulesModal from './EmailRulesModal';
import SocialNetworksModal from './SocialNetworksModal';
import AutoContactsModal from './AutoContactsModal';
import RecurringTasksModal from './RecurringTasksModal';
import WorkCentresModal from './WorkCentresModal';
import MfaSetupModal from './MfaSetupModal';
import UserActivityModal from './UserActivityModal';
import MileageRatesModal from './MileageRatesModal';
import DebtCollectionModal from './DebtCollectionModal';
import CloneSettingsModal from './CloneSettingsModal';
import InvoiceStatusSettingsModal from './InvoiceStatusSettingsModal';
import LeadSpamModal from './LeadSpamModal';

export default function SettingsDrawer({ isOpen, onClose, rowGroups, onRefresh, onApplyView, textScale, onTextScaleChange }) {
  const { user } = useAuth();
  const userMgmtDisclosure = useDisclosure();
  const backupDisclosure = useDisclosure();
  const adminSettingsDisclosure = useDisclosure();
  const bankAccountsDisclosure = useDisclosure();
  const vatRatesDisclosure = useDisclosure();
  const currencyDisclosure = useDisclosure();
  const rowGroupsDisclosure = useDisclosure();
  const viewFiltersDisclosure = useDisclosure();
  const accessibilityDisclosure = useDisclosure();
  const emailAccountsDisclosure = useDisclosure();
  const emailRulesDisclosure = useDisclosure();
  const socialNetworksDisclosure = useDisclosure();
  const autoContactsDisclosure = useDisclosure();
  const recurringDisclosure = useDisclosure();
  const workCentresDisclosure = useDisclosure();
  const mfaDisclosure = useDisclosure();
  const activityDisclosure = useDisclosure();
  const mileageRatesDisclosure = useDisclosure();
  const debtCollectionDisclosure = useDisclosure();
  const cloneSettingsDisclosure = useDisclosure();
  const invoiceStatusDisclosure = useDisclosure();
  const leadSpamDisclosure = useDisclosure();

  return (
    <>
      <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader fontSize="md">Settings</DrawerHeader>
          <DrawerBody>
            <VStack spacing={2} align="stretch">
              {user?.role === 'admin' && (
                <>
                  <Button w="100%" size="sm" leftIcon={<FiUsers />} colorScheme="blue" variant="outline"
                    onClick={() => { onClose(); userMgmtDisclosure.onOpen(); }}>User Management</Button>
                  <Button w="100%" size="sm" leftIcon={<FiDatabase />} colorScheme="orange" variant="outline"
                    onClick={() => { onClose(); backupDisclosure.onOpen(); }}>Data Backup / Restore</Button>
                  <Button w="100%" size="sm" leftIcon={<FiSliders />} colorScheme="purple" variant="outline"
                    onClick={() => { onClose(); adminSettingsDisclosure.onOpen(); }}>System Settings</Button>
                  <Button w="100%" size="sm" leftIcon={<FiCreditCard />} colorScheme="cyan" variant="outline"
                    onClick={() => { onClose(); bankAccountsDisclosure.onOpen(); }}>Our Bank Accounts</Button>
                  <Button w="100%" size="sm" leftIcon={<FiPercent />} colorScheme="green" variant="outline"
                    onClick={() => { onClose(); vatRatesDisclosure.onOpen(); }}>Set VAT Rates</Button>
                  <Button w="100%" size="sm" leftIcon={<FiDollarSign />} colorScheme="yellow" variant="outline"
                    onClick={() => { onClose(); currencyDisclosure.onOpen(); }}>Currencies</Button>
                  <Button w="100%" size="sm" leftIcon={<FiGrid />} colorScheme="gray" variant="outline"
                    onClick={() => { onClose(); rowGroupsDisclosure.onOpen(); }}>Row Groups</Button>
                  <Button w="100%" size="sm" leftIcon={<FiBriefcase />} colorScheme="gray" variant="outline"
                    onClick={() => { onClose(); workCentresDisclosure.onOpen(); }}>Work Centres</Button>
                  <Button w="100%" size="sm" leftIcon={<FiFilter />} colorScheme="pink" variant="outline"
                    onClick={() => { onClose(); viewFiltersDisclosure.onOpen(); }}>View Filters</Button>
                  <Divider />
                  <Button w="100%" size="sm" leftIcon={<FiMail />} colorScheme="red" variant="outline"
                    onClick={() => { onClose(); emailAccountsDisclosure.onOpen(); }}>Email Accounts</Button>
                  <Button w="100%" size="sm" leftIcon={<FiGitMerge />} colorScheme="red" variant="outline"
                    onClick={() => { onClose(); emailRulesDisclosure.onOpen(); }}>Mail Rules</Button>
                  <Button w="100%" size="sm" leftIcon={<FiUsers />} colorScheme="teal" variant="outline"
                    onClick={() => { onClose(); socialNetworksDisclosure.onOpen(); }}>Social Networks</Button>
                  <Button w="100%" size="sm" leftIcon={<FiHeart />} colorScheme="pink" variant="outline"
                    onClick={() => { onClose(); autoContactsDisclosure.onOpen(); }}>Auto Contacts</Button>
                  <Button w="100%" size="sm" leftIcon={<FiRepeat />} colorScheme="purple" variant="outline"
                    onClick={() => { onClose(); recurringDisclosure.onOpen(); }}>Recurring Tasks</Button>
                  <Divider />
                  <Button w="100%" size="sm" leftIcon={<FiTruck />} colorScheme="blue" variant="outline"
                    onClick={() => { onClose(); mileageRatesDisclosure.onOpen(); }}>Mileage Rates</Button>
                  <Button w="100%" size="sm" leftIcon={<FiDollarSign />} colorScheme="red" variant="outline"
                    onClick={() => { onClose(); debtCollectionDisclosure.onOpen(); }}>Debt Collection</Button>
                  <Button w="100%" size="sm" leftIcon={<FiList />} colorScheme="teal" variant="outline"
                    onClick={() => { onClose(); invoiceStatusDisclosure.onOpen(); }}>Invoice Status Settings</Button>
                  <Button w="100%" size="sm" leftIcon={<FiCopy />} colorScheme="purple" variant="outline"
                    onClick={() => { onClose(); cloneSettingsDisclosure.onOpen(); }}>Project Templates / Clones</Button>
                  <Button w="100%" size="sm" leftIcon={<FiActivity />} colorScheme="red" variant="outline"
                    onClick={() => { onClose(); activityDisclosure.onOpen(); }}>User Activity Log</Button>
                  <Button w="100%" size="sm" leftIcon={<FiSlash />} colorScheme="red" variant="solid"
                    onClick={() => { onClose(); leadSpamDisclosure.onOpen(); }}>Lead Spam</Button>
                  <Divider />
                </>
              )}

              <Button w="100%" size="sm" leftIcon={<FiEye />} colorScheme="blue" variant="outline"
                onClick={() => { onClose(); accessibilityDisclosure.onOpen(); }}>Accessibility</Button>
              <Button w="100%" size="sm" leftIcon={<FiShield />} colorScheme="green" variant="outline"
                onClick={() => { onClose(); mfaDisclosure.onOpen(); }}>Two-Factor Auth (MFA)</Button>
            </VStack>
          </DrawerBody>
          <DrawerFooter>
            <Button size="sm" onClick={onClose}>Close</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <UserManagementModal isOpen={userMgmtDisclosure.isOpen} onClose={userMgmtDisclosure.onClose} />
      <BackupRestoreModal isOpen={backupDisclosure.isOpen} onClose={backupDisclosure.onClose} />

      <AdminSettingsModal isOpen={adminSettingsDisclosure.isOpen} onClose={adminSettingsDisclosure.onClose} />
      <BankAccountsModal isOpen={bankAccountsDisclosure.isOpen} onClose={bankAccountsDisclosure.onClose}
        isOwn={true} title="Our Bank Accounts" />
      <VatRatesModal isOpen={vatRatesDisclosure.isOpen} onClose={vatRatesDisclosure.onClose} />
      <CurrencyModal isOpen={currencyDisclosure.isOpen} onClose={currencyDisclosure.onClose} />
      <RowGroupsModal isOpen={rowGroupsDisclosure.isOpen} onClose={rowGroupsDisclosure.onClose} onRefresh={onRefresh} />
      <ViewFiltersModal isOpen={viewFiltersDisclosure.isOpen} onClose={viewFiltersDisclosure.onClose}
        rowGroups={rowGroups} onApply={onApplyView} />
      <AccessibilityModal isOpen={accessibilityDisclosure.isOpen} onClose={accessibilityDisclosure.onClose}
        currentScale={textScale} onScaleChange={onTextScaleChange} />
      <EmailAccountsModal isOpen={emailAccountsDisclosure.isOpen} onClose={emailAccountsDisclosure.onClose} />
      <EmailRulesModal isOpen={emailRulesDisclosure.isOpen} onClose={emailRulesDisclosure.onClose} />
      <SocialNetworksModal isOpen={socialNetworksDisclosure.isOpen} onClose={socialNetworksDisclosure.onClose} />
      <AutoContactsModal isOpen={autoContactsDisclosure.isOpen} onClose={autoContactsDisclosure.onClose} />
      <RecurringTasksModal isOpen={recurringDisclosure.isOpen} onClose={recurringDisclosure.onClose} />
      <WorkCentresModal isOpen={workCentresDisclosure.isOpen} onClose={workCentresDisclosure.onClose} />
      <MfaSetupModal isOpen={mfaDisclosure.isOpen} onClose={mfaDisclosure.onClose}
        mfaEnabled={!!user?.mfa_enabled} onUpdate={() => {}} />
      <UserActivityModal isOpen={activityDisclosure.isOpen} onClose={activityDisclosure.onClose} />
      <MileageRatesModal isOpen={mileageRatesDisclosure.isOpen} onClose={mileageRatesDisclosure.onClose} />
      <DebtCollectionModal isOpen={debtCollectionDisclosure.isOpen} onClose={debtCollectionDisclosure.onClose} />
      <CloneSettingsModal isOpen={cloneSettingsDisclosure.isOpen} onClose={cloneSettingsDisclosure.onClose} />
      <InvoiceStatusSettingsModal isOpen={invoiceStatusDisclosure.isOpen} onClose={invoiceStatusDisclosure.onClose} />
          <LeadSpamModal isOpen={leadSpamDisclosure.isOpen} onClose={leadSpamDisclosure.onClose} />
    </>
  );
}
